import React from 'react';
import styles from './styles.module.css';
import { useState, useContext, useCallback } from 'react';
import {
  TextField,
  MenuItem,
  Button,
  CardMedia,
  FormControlLabel,
  Switch,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  TAX_PREFERENCES,
  TAX_REGIONS,
  SHOP_SUB_CATEGORIES,
  SHOP_CATEGORIES,
  CONFIGS,
  SHOP_SETTINGS,
  ONLINE_ORDER_PROCESSING_TYPE,
  TAX_TYPE,
  ROUND_OFF_TYPE,
  OFFLINE_REASON,
  ORDER_PAYMENT_COLLECTION_MODES,
  DEFAULT_ORDER_PAYMENT_COLLECTION_MODE,
  ADVANCE_PAYMENT_TYPES,
  BARCODE_PRINTER_FORMATS,
  BARCODE_PRINTER_FORMATS_CUSTOM_PRN,
  INVOICE_LAYOUTS,
} from 'const';
import { AlertMessagesContext } from 'react-alert-messages';
import { ShopContext } from 'Context/ShopContext';
import ShopsHelper from 'helpers/ShopsHelper';
import ShopSettingsService from 'services/ShopSettingsService';
import { useEffect } from 'react';
import { WarehouseContext } from 'Context/WarehouseContext';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import FeatureNotAvailableDialog from 'components/popups/FeatureNotAvailableDialog';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import TerminalService from 'services/TerminalServices';
import SupportService from 'services/SupportService';
import { ThemeContext } from 'Context/ThemeContext';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIpDialog from 'components/popups/AddIpDialog';
import AddCustomPrnTemplateDialog from 'components/popups/AddCustomPrnTemplateDialog';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

function ShopSettings() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [data, setData] = useState({
    online_order_terminal: '',
    online_order_payment_mode: '',
    online_order_processing_type: '',
    config: {},
  });
  const [featureNotAvailable, setFeatureNotAvailable] = useState(null);
  const [imageData, setImageData] = useState('');
  const [externalTokenKey, setExternalTokenKey] = useState('');
  const [updateTerminalSettingsDialog, setUpdateTerminalSettingsDialog] = useState(false);
  const [shopCategory, setShopCategory] = useState({});
  const [supportEnabled, setSupportEnabled] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [showAddIpDialog, setShowAddIpDialog] = useState(false);
  const [selectedPrinterIp, setSelectedPrinterIp] = useState(null);
  const [showAddTemplateDialog, setShowAddTemplateDialog] = useState(false);
  const [selectedTemplateForEdit, setSelectedTemplateForEdit] = useState(null);

  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { theme } = useContext(ThemeContext);
  const { shop, setShop } = useContext(ShopContext);
  const { getWarehouses, warehouses } = useContext(WarehouseContext);

  const closeDialogHandler = () => {
    setFeatureNotAvailable(null);
  };

  const uploadImage = (image) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(image);
      reader.onloadend = async () => {
        const logo = {
          image: reader.result,
        };
        await ShopSettingsService.uploadShopLogo(logo);
        resolve();
      };
    });
  };

  const removeShopLogo = () => {
    setImageData('');
    setData({ ...data, shop_logo: null });
  };

  const handleTerminalSettingsUpdate = async () => {
    setLoadingIndicator(true);
    try {
      const res = await ShopSettingsService.updateShopSettings({
        category: data?.category,
        sub_category: data?.sub_category,
      });
      setData(res);
      setShop(res);
      setShopCategory({ category: res.category, sub_category: res.sub_category });

      const terminals = await TerminalService.getTerminals();
      await Promise.all(
        terminals.map(async (terminal) => {
          await TerminalService.updateTerminals(terminal.id, SHOP_SETTINGS[data?.sub_category]);
        })
      );
      setUpdateTerminalSettingsDialog(false);
      postAlertMessage({ text: tManagement('Updated shop settings successfully'), type: 'success' });
    } catch (error) {
      postAlertMessage({ text: tManagement('Attempt failed'), type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const updateShopSettings = async (updatedShopData = null) => {
    setLoadingIndicator(true);
    try {
      const dataToSave = updatedShopData && updatedShopData.config ? updatedShopData : data;
      const payload = {
        ...dataToSave,
        config: dataToSave.config,
      };
      const res = await ShopSettingsService.updateShopSettings(payload);
      if (imageData && imageData.size < CONFIGS.LOGO_MAX_SIZE) {
        await uploadImage(imageData);
      }
      setData({
        ...res,
        config: res.config || {},
      });
      setShop(res);
      postAlertMessage({ text: tManagement('Updated shop settings successfully'), type: 'success' });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Attempt failed';
      postAlertMessage({ text: errorMessage, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const isChangeShopCategory = useCallback(() => {
    return data?.category !== shopCategory?.category || data?.sub_category !== shopCategory?.sub_category;
  }, [data?.category, data?.sub_category, shopCategory?.category, shopCategory?.sub_category]);

  const resetExtTknBtnPressed = async () => {
    setLoadingIndicator(true);
    try {
      const data = await ShopSettingsService.resetExternalTokenKey();
      const externalTokenKey = data.ext_access_token;
      setExternalTokenKey(externalTokenKey);
      postAlertMessage({ text: tManagement('Successfully updated!'), type: 'success' });
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const copyExternalToken = async () => {
    const copyText = document.getElementById('extTkn');
    copyText.select();
    copyText.setSelectionRange(0, 99999);
    navigator.clipboard.writeText(copyText.value);
    postAlertMessage({ text: tManagement('Copied external token'), type: 'success' });
  };

  const handleChangeSupportMode = async (event) => {
    const { checked } = event.target;
    const data = await SupportService.changeSupport({
      flavor: theme.variable.name,
      enabled: checked,
    });
    setSupportEnabled(data);
  };

  const handleSaveTemplateSuccess = (templateData) => {
    const templates = data?.config?.barcode_settings?.barcode_custom_prn_templates || [];
    let updatedTemplates = [...templates];
    let newActiveId = data?.config?.barcode_settings?.active_template_id || '';
    let newDefaultPrn = data?.config?.barcode_settings?.barcode_custom_prn || '';

    if (selectedTemplateForEdit !== null) {
      const index = selectedTemplateForEdit.index;
      updatedTemplates[index] = { ...updatedTemplates[index], ...templateData };
      if (updatedTemplates[index].id === newActiveId) {
        newDefaultPrn = templateData.code;
      }
    } else {
      const newId = Date.now().toString();
      const newTpl = { id: newId, ...templateData };
      updatedTemplates.push(newTpl);
      if (templates.length === 0) {
        newActiveId = newId;
        newDefaultPrn = newTpl.code;
      }
    }

    const updatedShopData = {
      ...data,
      config: {
        ...data.config,
        barcode_settings: {
          ...(data.config.barcode_settings || {}),
          barcode_custom_prn_templates: updatedTemplates,
          active_template_id: newActiveId,
          barcode_custom_prn: newDefaultPrn,
        }
      }
    };
    setData(updatedShopData);
    updateShopSettings(updatedShopData);
    setSelectedTemplateForEdit(null);
  };

  const handleDeleteTemplate = (templateId) => {
    const templates = data?.config?.barcode_settings?.barcode_custom_prn_templates || [];
    const activeTemplateId = data?.config?.barcode_settings?.active_template_id || '';
    const updatedTemplates = templates.filter((t) => t.id !== templateId);
    const isActiveDeleted = templateId === activeTemplateId;
    const newActiveId = isActiveDeleted ? (updatedTemplates[0]?.id || '') : activeTemplateId;
    const newDefaultPrn = isActiveDeleted ? (updatedTemplates[0]?.code || '') : (data?.config?.barcode_settings?.barcode_custom_prn || '');

    const updatedShopData = {
      ...data,
      config: {
        ...data.config,
        barcode_settings: {
          ...(data?.config?.barcode_settings || {}),
          barcode_custom_prn_templates: updatedTemplates,
          active_template_id: newActiveId,
          barcode_custom_prn: newDefaultPrn,
        },
      },
    };
    setData(updatedShopData);
    updateShopSettings(updatedShopData);
    postAlertMessage({ text: tManagement('Template deleted successfully'), type: 'success' });
  };

  const handleChangeMode = (event) => {
    const { name, checked } = event.target;
    const updatedData = {
      ...data,
      [name]: checked,
    };

    setData(updatedData);
  };

  const handleChangeConfig = (event) => {
    const { name, checked } = event.target;
    const updatedConfig = {
      ...data.config,
      [name]: checked,
    };
    setData((prevData) => ({
      ...prevData,
      config: updatedConfig,
    }));
  };

  const checkSupport = useCallback(async () => {
    try {
      const data = await SupportService.checkSupport(theme.variable.name);
      setSupportEnabled(data);
    } catch (error) {
      postAlertMessage({ text: tManagement('Failed to check support.'), type: 'failed' });
    }
  }, [theme.variable.name, postAlertMessage, tManagement]);

  const getTerminals = useCallback(async () => {
    try {
      const resp = await TerminalService.getTerminals();
      setTerminals(resp);
    } catch (error) {
      postAlertMessage({ text: tManagement('Failed to load terminals.'), type: 'failed' });
    }
  }, [postAlertMessage, tManagement]);

  const getPaymentModes = useCallback(async () => {
    try {
      const resp = await ShopSettingsService.getPaymentModes({ all: true });
      setPaymentModes(resp);
    } catch (error) {
      postAlertMessage({ text: tManagement('Failed to load payment modes.'), type: 'failed' });
    }
  }, [postAlertMessage, tManagement]);

  const getShopSettings = useCallback(async () => {
    try {
      const resp = await ShopSettingsService.getShopSettings();
      const config = resp.config || {};
      if (config.default_warehouse && typeof config.default_warehouse === 'object') {
        config.default_warehouse = config.default_warehouse.id;
      }
      setData({
        ...resp,
        config: config,
      });
      setShopCategory({
        category: resp?.category,
        sub_category: resp?.sub_category,
      });
    } catch (error) {
      postAlertMessage({ text: error?.message || tManagement('Failed to load settings.'), type: 'failed' });
    }
  }, [postAlertMessage, tManagement]);

  const loadExternalTokenKey = useCallback(async () => {
    try {
      const data = await ShopSettingsService.getExternalTokenKey();
      setExternalTokenKey(data.ext_access_token);
    } catch (error) {
      postAlertMessage({ text: 'Failed to load token key.', type: 'failed' });
    }
  }, [postAlertMessage]);

  const loadAllData = useCallback(async () => {
    setLoadingIndicator(true);
    try {
      await Promise.all([
        loadExternalTokenKey(),
        checkSupport(),
        getShopSettings(),
        getTerminals(),
        getPaymentModes(),
        getWarehouses(),
      ]);
    } finally {
      setLoadingIndicator(false);
    }
  }, [
    loadExternalTokenKey,
    checkSupport,
    getShopSettings,
    getTerminals,
    getPaymentModes,
    getWarehouses,
  ]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const FEATURE_OPTIONS = [
    { key: 'cash_box', label: 'Cash Box' },
    { key: 'reprint', label: 'Reprint' },
    { key: 'delete_cart', label: 'Delete Cart' },
    { key: 'buy_back', label: 'Buy Back' },
    { key: 'report', label: 'Report' },
    { key: 'price', label: 'Price' },
    { key: 'discount', label: 'Discount' },
    { key: 'round_off', label: 'Round Off' },
    { key: 'product_edit', label: 'Product Edit' },
    { key: 'sale_edit', label: 'Sale Edit' },
  ];

  return (
    <div className={styles.contentLayout}>
      <div className={styles.settingsGrid}>
        <Grid container rowGap={1.5}>
          <Grid item container xs={12} columnSpacing={1} rowSpacing={1.5} className={styles.sessionContainer}>
            <Grid item xs={12} className={styles.subHeader}>
              <Typography variant="h8" gutterBottom>
                {tCommon('Shop Config')}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                {tCommon('Configure essential shop details and operational preferences.')}
              </Typography>{' '}
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Shop Name')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="shopName"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({
                      ...data,
                      shop_name: event.target.value,
                    });
                  }}
                  value={data?.shop_name}
                />
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Mobile Number')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="mobile_no"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  inputProps={{ maxLength: 15 }}
                  onChange={(event) => {
                    setData({
                      ...data,
                      mobile_no: event.target.value.slice(0, 15),
                    });
                  }}
                  value={data?.mobile_no}
                />
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Currency')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="currency"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({
                      ...data,
                      currency: event.target.value,
                    });
                  }}
                  value={data?.currency}
                />
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Currency Symbol')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="currencySymbol"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({
                      ...data,
                      currency_symbol: event.target.value,
                    });
                  }}
                  value={data?.currency_symbol}
                />
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Currency Name')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="currencyName"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({
                      ...data,
                      currency_name: event.target.value,
                    });
                  }}
                  value={data?.currency_name}
                />
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Currency Fraction Name')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="currencyFractionName"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({
                      ...data,
                      currency_fraction_name: event.target.value,
                    });
                  }}
                  value={data?.currency_fraction_name}
                />
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Round Off Type')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  name="roundOffType"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({ ...data, automatic_round_off_type: event.target.value });
                  }}
                  value={data?.automatic_round_off_type || ' '}
                >
                  {ROUND_OFF_TYPE.map((round_off_type) => {
                    let [key, value] = Object.entries(round_off_type)[0];
                    return (
                      <MenuItem key={key} value={value}>
                        {key}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Round Off Precision')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="roundOffPrecision"
                  size="small"
                  type="number"
                  inputProps={{ step: 'any' }}
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({
                      ...data,
                      automatic_round_off_precision: event.target.value,
                    });
                  }}
                  value={data?.automatic_round_off_precision}
                />
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Tax Region')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  name="taxRegion"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({ ...data, tax_region: event.target.value });
                  }}
                  value={data?.tax_region || 'India'}
                >
                  {TAX_REGIONS.map((tax_region) => {
                    let [key, value] = Object.entries(tax_region)[0];
                    return (
                      <MenuItem key={key} value={value}>
                        {key}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Tax Type')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  name="taxType"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({ ...data, tax_type: event.target.value });
                  }}
                  value={data?.tax_type || ' '}
                >
                  {TAX_TYPE.map((tax_type) => {
                    let [key, value] = Object.entries(tax_type)[0];
                    return (
                      <MenuItem key={key} value={value}>
                        {key}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Tax Preference')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  name="taxPreference"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({ ...data, tax_preference: event.target.value });
                  }}
                  value={data?.tax_preference || 'Excluding Tax'}
                >
                  {TAX_PREFERENCES.map((tax_preference) => {
                    let [key, value] = Object.entries(tax_preference)[0];
                    return (
                      <MenuItem key={key} value={value}>
                        {key}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Discount display order')}</span>
              </Grid>
              <Grid item xs={12}>
                <NumberField
                  name="discountOrder"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({
                      ...data,
                      config: {
                        ...data.config,
                        payment_mode_order: {
                          ...data?.config?.payment_mode_order,
                          discount: Number(event.target.value),
                        },
                      },
                    });
                  }}
                  value={data?.config?.payment_mode_order?.discount || null}
                />
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex" alignItems="center">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Customer credit display order')}</span>
              </Grid>
              <Grid item xs={12}>
                <NumberField
                  name="creditOrder"
                  color="primary"
                  onChange={(event) => {
                    setData({
                      ...data,
                      config: {
                        ...data.config,
                        payment_mode_order: { ...data?.config?.payment_mode_order, credit: Number(event.target.value) },
                      },
                    });
                  }}
                  value={data?.config?.payment_mode_order?.credit || null}
                />
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flow">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Web Invoice Layout')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  value={data?.web_invoice_format || INVOICE_LAYOUTS.A4_BACKEND}
                  onChange={(e) => {
                    setData({
                      ...data,
                      web_invoice_format: e.target.value,
                    });
                  }}
                >
                  <MenuItem value="a4_backend">A4 </MenuItem>
                  <MenuItem value="a5_backend">A5 </MenuItem>
                  <MenuItem value="a4_frontend">Invoice A4 </MenuItem>
                  <MenuItem value="a5_portrait_frontend">Invoice A5 Portrait </MenuItem>
                  <MenuItem value="a5_landscape_frontend">Invoice A5 Landscape </MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flow">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('Barcode Printer Format')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  name="creditOrder"
                  size="small"
                  type="number"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({
                      ...data,
                      config: {
                        ...data.config,
                        barcode_settings: {
                          ...(data?.config?.barcode_settings || {}),
                          barcode_printer_format: event.target.value,
                        },
                      },
                    });
                  }}
                  value={data?.config?.barcode_settings?.barcode_printer_format || null}
                >
                  {BARCODE_PRINTER_FORMATS.map((barcode_printer_format) => (
                    <MenuItem key={barcode_printer_format} value={barcode_printer_format}>
                      {barcode_printer_format}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flow">
              <Grid item xs={12}>
                <span className={styles.fieldTitle}>{tCommon('UPI ID')}</span>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="upiId"
                  size="small"
                  type="text"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  placeholder="example@upi"
                  value={data?.config?.upi_id || ""}
                  onChange={(event) => {
                    setData({
                      ...data,
                      config: {
                        ...data.config,
                        upi_id: event.target.value,
                      },
                    });
                  }}
                />
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={4} display="flex">
              <Grid item xs={10}>
                <span className={styles.fieldTitle}>{tCommon('Stock Availability Check For Sale')}</span>
              </Grid>
              <Grid item xs={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data?.stock_availability_check_for_sale || false}
                      onChange={handleChangeMode}
                      name={'stock_availability_check_for_sale'}
                      color="primary"
                    />
                  }
                />
              </Grid>
            </Grid>
            {data?.config?.barcode_settings?.barcode_printer_format === BARCODE_PRINTER_FORMATS_CUSTOM_PRN && (
              <Grid
                container
                xs={12}
                alignItems="center"
                justifyContent="space-between"
                style={{
                  backgroundColor: '#f9f9f9',
                  padding: '16px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  marginTop: '16px',
                  marginBottom: '16px',
                }}
              >
                <Grid item xs={12} lg={8} className={styles.subHeader}>
                  <Typography variant="h8" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {tCommon('Custom Barcode Design')}
                  </Typography>
                  <Typography variant="subtitle2" color="textSecondary">
                    {tCommon('Manage custom barcode label templates')}
                  </Typography>
                </Grid>
                <Grid item xs={12} lg={4} textAlign="end">
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={() => {
                      setSelectedTemplateForEdit(null);
                      setShowAddTemplateDialog(true);
                    }}
                  >
                    <AddCircleOutlineIcon style={{ marginRight: '6px' }} />
                    {tCommon('Add PRN')}
                  </Button>
                </Grid>

                <Grid container sx={{ mt: 2 }}>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {tCommon('Template Name')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {tCommon('PRN Code')}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', textAlign: 'center' }}>
                      {tCommon('Actions')}
                    </Typography>
                  </Grid>

                  <Grid container spacing={1} mt={1}>
                    {(() => {
                      const templates = data?.config?.barcode_settings?.barcode_custom_prn_templates || [];
                      const activeTemplateId = data?.config?.barcode_settings?.active_template_id || '';

                      if (templates.length === 0) {
                        return (
                          <Grid item xs={12}>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              style={{ textAlign: 'center', padding: '20px 0' }}
                            >
                              {tCommon('No templates found')}
                            </Typography>
                          </Grid>
                        );
                      }

                      return templates.map((tpl, index) => (
                        <Grid
                          container
                          item
                          xs={12}
                          key={tpl.id}
                          alignItems="center"
                          style={{
                            position: 'relative',
                            borderBottom: '1px solid #f0f0f0',
                            paddingBottom: '8px',
                            paddingTop: '8px',
                          }}
                        >
                          <IconButton
                            size="small"
                            style={{
                              position: 'absolute',
                              top: '2px',
                              right: '0px',
                              color: tpl.id === activeTemplateId ? '#00a65a' : '#ccc',
                            }}
                            onClick={() => {
                              const newActiveId = tpl.id === activeTemplateId ? '' : tpl.id;
                              const newDefaultPrn = tpl.id === activeTemplateId ? '' : tpl.code;
                              const updatedShopData = {
                                ...data,
                                config: {
                                  ...data.config,
                                  barcode_settings: {
                                    ...(data.config.barcode_settings || {}),
                                    active_template_id: newActiveId,
                                    barcode_custom_prn: newDefaultPrn,
                                  },
                                },
                              };
                              setData(updatedShopData);
                              updateShopSettings(updatedShopData);
                            }}
                          >
                            {tpl.id === activeTemplateId ? <CheckCircleIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
                          </IconButton>

                          <Grid item xs={4} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography variant="body2">{tpl.name}</Typography>
                            {tpl.id === activeTemplateId && (
                              <span style={{ fontSize: '10px', fontWeight: 'bold', marginTop: '2px' }}>
                                {tCommon('default')}
                              </span>
                            )}
                          </Grid>
                          <Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              noWrap
                              style={{
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                maxWidth: '90%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {tpl.code}
                            </Typography>
                          </Grid>
                          <Grid item xs={2} style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                            <Button
                              style={{
                                minWidth: '40px',
                                padding: '4px',
                                color: '#4d77ff',
                                borderColor: '#4d77ff',
                              }}
                              variant="outlined"
                              onClick={() => {
                                setSelectedTemplateForEdit({ ...tpl, index });
                                setShowAddTemplateDialog(true);
                              }}
                            >
                              <EditIcon />
                            </Button>
                            <Button
                              style={{
                                minWidth: '40px',
                                padding: '4px',
                                color: 'red',
                                borderColor: 'red',
                              }}
                              variant="outlined"
                              onClick={() => handleDeleteTemplate(tpl.id)}
                            >
                              <DeleteOutlineIcon />
                            </Button>
                          </Grid>
                        </Grid>
                      ));
                    })()}
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid container spacing={2} mt={2} mb={2}>
              <Grid item xs={12} md={3}>
                <div className={styles.logoTitle}>
                  {tCommon('Shop Logo')}
                </div>

                <div className={styles.logoSubTitle}>
                  {tCommon('150px × 150px size recommended')}
                </div>
              </Grid>

              <Grid item xs={12} md={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <div className={styles.logoActions}>
                      <Button
                        variant="contained"
                        size="small"
                        component="label"
                        className={styles.uploadBtn}
                      >
                        {imageData || data?.shop_logo
                          ? tCommon('Change Logo')
                          : tCommon('Select Shop Logo')}

                        <input
                          hidden
                          accept="image/*"
                          type="file"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              setImageData(e.target.files[0]);
                            }
                          }}
                        />
                      </Button>

                      {(imageData || data?.shop_logo) && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          onClick={removeShopLogo}
                        >
                          {tCommon('Delete')}
                        </Button>
                      )}
                    </div>
                  </Grid>

                  {(imageData || data?.shop_logo) && (
                    <Grid item xs={12} lg={6}>
                      <CardMedia
                        fullWidth
                        component="img"
                        image={
                          imageData
                            ? URL.createObjectURL(imageData)
                            : data?.shop_logo
                        }
                        className={styles.categoryImg}
                      />
                    </Grid>
                  )}

                  {imageData && imageData.size > CONFIGS.LOGO_MAX_SIZE && (
                    <Grid item xs={12}>
                      <div className={styles.imageSizeWarning}>
                        {tManagement(
                          '*Image size exceeds maximum limit. Upload file size less than 200KB'
                        )}
                      </div>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            {shop.kot_validity && (
              <Grid
                container
                xs={12}
                alignItems="center"
                justifyContent="space-between"
                style={{
                  backgroundColor: '#f9f9f9',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Grid item xs={12} lg={8} className={styles.subHeader}>
                  <Typography variant="h8" gutterBottom>
                    {tCommon('IP Printer Configuration')}
                  </Typography>
                  <Typography variant="subtitle2" color="textSecondary">
                    {tCommon('Define printer ip addresses configuration')}
                  </Typography>
                </Grid>
                <Grid item xs={12} lg={4} textAlign="end">
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={() => {
                      setSelectedPrinterIp(null);
                      setShowAddIpDialog(true);
                    }}
                  >
                    <AddCircleOutlineIcon style={{ marginRight: '6px' }} />
                    {tCommon('Add Printer')}
                  </Button>
                </Grid>

                <Grid container>
                  <Grid item xs={5}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {tCommon('PrinterName')}
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {tCommon('IP Address')}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', textAlign: 'center' }}>
                      {tCommon('Actions')}
                    </Typography>
                  </Grid>

                  <Grid container spacing={1} mt={1}>
                    {(data?.config?.ip_printers || []).length === 0 ? (
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ textAlign: 'center', padding: '20px 0' }}
                        >
                          {tCommon('No IP printers found')}
                        </Typography>
                      </Grid>
                    ) : (
                      (data?.config?.ip_printers || []).map((printerIp, index) => (
                        <React.Fragment key={index}>
                          <Grid item xs={12} lg={5}>
                            <TextField
                              size="small"
                              variant="outlined"
                              fullWidth
                              value={printerIp.name || ''}
                              onChange={(e) => {
                                const newPrinterIps = [...(data?.config?.ip_printers || [])];
                                newPrinterIps[index] = { ...newPrinterIps[index], name: e.target.value };
                                setData((prev) => ({
                                  ...prev,
                                  config: { ...prev.config, ip_printers: newPrinterIps },
                                }));
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} lg={5}>
                            <TextField
                              size="small"
                              variant="outlined"
                              fullWidth
                              value={printerIp.ip_address || ''}
                              onChange={(e) => {
                                const newPrinterIps = [...(data?.config?.ip_printers || [])];
                                newPrinterIps[index] = { ...newPrinterIps[index], ip_address: e.target.value };
                                setData((prev) => ({
                                  ...prev,
                                  config: { ...prev.config, ip_printers: newPrinterIps },
                                }));
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} lg={2} style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                            <Button
                              style={{
                                minWidth: '40px',
                                padding: '4px',
                                color: '#4d77ff',
                                borderColor: '#4d77ff',
                              }}
                              variant="outlined"
                              onClick={() => {
                                setSelectedPrinterIp({ ...printerIp, index });
                                setShowAddIpDialog(true);
                              }}
                            >
                              <EditIcon />
                            </Button>
                            <Button
                              style={{
                                minWidth: '40px',
                                padding: '4px',
                                color: '#ff4d4f',
                                borderColor: '#ff4d4f',
                              }}
                              variant="outlined"
                              onClick={() => {
                                const newPrinterIps = (data?.config?.ip_printers || []).filter((_, i) => i !== index);
                                const updatedShopData = {
                                  ...data,
                                  config: { ...data.config, ip_printers: newPrinterIps },
                                };
                                setData(updatedShopData);
                                updateShopSettings(updatedShopData);
                              }}
                            >
                              <DeleteOutlineIcon />
                            </Button>
                          </Grid>
                        </React.Fragment>
                      ))
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}
            <hr className={styles.divider} />
            <Grid item xs={12} className={styles.subHeader}>
              <Typography variant="h8" gutterBottom>
                {tCommon('Set Staff-Level Permissions')}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                {tCommon('Manage what actions your staff are allowed to perform on the POS.')}
              </Typography>{' '}
            </Grid>
            <Grid item container xs={12} lg={12} display="flex" alignItems="center">
              {FEATURE_OPTIONS.map(({ key, label }) => (
                <Grid item container xs={12} lg={4} display="flex" alignItems="center" padding="8px 0 0 8px" key={key}>
                  <Grid item xs={12}>
                    <span className={styles.fieldTitle}>{label}</span>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name={key}
                      type="text"
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      inputProps={{
                        maxLength: 4,
                        inputMode: 'numeric',
                      }}
                      placeholder={tCommon('Enter a 4-digit password')}
                      value={data.config?.staff_access_codes?.[key] ?? ''}
                      onChange={(e) =>
                        setData((prev) => ({
                          ...prev,
                          config: {
                            ...(prev?.config || {}),
                            staff_access_codes: {
                              ...(prev?.config?.staff_access_codes || {}),
                              [key]: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                  </Grid>
                </Grid>
              ))}
            </Grid>
            {!featureIsExpired(shop?.warehouse_validity) && (
              <>
                <hr className={styles.divider} />
                <Grid item xs={12} className={styles.subHeader}>
                  <Typography variant="h8" gutterBottom>
                    {tCommon('Warehouse Settings')}
                  </Typography>
                  <Typography variant="subtitle2" color="textSecondary">
                    {tCommon('Manage warehouse configuration and default settings.')}
                  </Typography>
                </Grid>
                <Grid item container xs={12} lg={12} display="flex" alignItems="center">
                  <Grid item container xs={12} lg={4} display="flex" alignItems="center" padding="8px 0 0 8px">
                    <Grid item xs={10}>
                      <span className={styles.fieldTitle}>{tCommon('Warehouse Enabled')}</span>
                    </Grid>
                    <Grid item xs={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={data?.config?.is_warehouse_enabled || false}
                            onChange={handleChangeConfig}
                            name={'is_warehouse_enabled'}
                            color="primary"
                          />
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid item container xs={12} lg={4} display="flow" padding="8px 0 0 8px">
                    <Grid item xs={12}>
                      <span className={styles.fieldTitle}>{tCommon('Default Warehouse')}</span>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        select
                        name="default_warehouse"
                        size="small"
                        variant="outlined"
                        color="primary"
                        fullWidth
                        value={data?.config?.default_warehouse || 'null'}
                        onChange={(event) => {
                          setData((prevData) => ({
                            ...prevData,
                            config: {
                              ...prevData.config,
                              default_warehouse: event.target.value === 'null' ? null : Number(event.target.value),
                            },
                          }));
                        }}
                      >
                        <MenuItem value="null">{tCommon('None')}</MenuItem>
                        {warehouses?.map((warehouse) => (
                          <MenuItem key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
            <hr className={styles.divider} />
            {ShopsHelper.hasPremiumAccess(shop) && (
              <>
                <Grid item xs={12} className={styles.subHeader}>
                  <Typography variant="h8" gutterBottom>
                    {tCommon('Loyalty Config')}
                  </Typography>
                  <Typography variant="subtitle2" color="textSecondary">
                    {tCommon('Set how customers earn and redeem loyalty points on the POS.')}
                  </Typography>
                </Grid>
                <Grid item container xs={12} lg={6} display="flex" alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <span className={styles.fieldTitle}>{tCommon('Amount Spent For One Loyalty Point')}</span>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <NumberField
                      name="amountSpentForLoyalty"
                      color="primary"
                      onChange={(event) => {
                        setData({
                          ...data,
                          amount_spent_for_loyalty_point: Number(event.target.value),
                        });
                      }}
                      value={data?.amount_spent_for_loyalty_point}
                    />
                  </Grid>
                </Grid>
                <Grid item container xs={12} lg={6} display="flex" alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <span className={styles.fieldTitle}>{tCommon('Loyalty Point Conversion Rate')}</span>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <NumberField
                      name="loyaltyPointConversionRate"
                      color="primary"
                      onChange={(event) => {
                        setData({
                          ...data,
                          loyalty_point_conversion_rate: Number(event.target.value),
                        });
                      }}
                      value={data?.loyalty_point_conversion_rate}
                    />
                  </Grid>
                </Grid>
              </>
            )}
            <hr className={styles.divider} />
            {shop.online_cart_validity && (
              <>
                <Grid item xs={12} className={styles.subHeader}>
                  <Typography variant="h8" gutterBottom>
                    {tCommon('Online Order Config')}
                  </Typography>
                  <Typography variant="subtitle2" color="textSecondary">
                    {tCommon('Set up how online orders are received and processed.')}
                  </Typography>
                </Grid>
                <Grid item container xs={12} lg={6} display="flex" alignItems="center">
                  <Grid item xs={10} sm={6}>
                    <span className={styles.fieldTitle}>{tCommon('Sanoft Cart: Online / Offline')}</span>
                  </Grid>
                  <Grid item xs={2} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={data?.config?.is_sanoft_cart_enabled || false}
                          onChange={handleChangeConfig}
                          name={'is_sanoft_cart_enabled'}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>
                <Grid item container xs={12} lg={6} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('Store Offline Reason')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      name="offlineReason"
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onChange={(event) => {
                        setData({ ...data, config: { ...data.config, offline_reason: event.target.value } });
                      }}
                      value={data?.config?.offline_reason || ''}
                    >
                      <MenuItem value="">{tCommon('None')}</MenuItem>
                      {Object.keys(OFFLINE_REASON).map((reason) => {
                        return (
                          <MenuItem key={reason} value={reason}>
                            {OFFLINE_REASON[reason]}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </Grid>
                </Grid>
                <Grid item container xs={12} lg={6} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('Terminal')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      name="onlineOrderTerminal"
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onChange={(event) => {
                        setData((prevData) => ({
                          ...prevData,
                          online_order_terminal: event.target.value,
                        }));
                      }}
                      value={data.online_order_terminal || ''}
                    >
                      {terminals.map((terminal) => {
                        return (
                          <MenuItem key={terminal.id} value={terminal.id}>
                            {terminal.name}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </Grid>
                </Grid>
                <Grid item container xs={12} lg={6} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('Payment Mode')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      name="onlineOrderPaymentMode"
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onChange={(event) => {
                        setData((prevData) => ({
                          ...prevData,
                          online_order_payment_mode: event.target.value,
                        }));
                      }}
                      value={data?.online_order_payment_mode || ''}
                    >
                      {paymentModes.map((pm) => {
                        return (
                          <MenuItem key={pm.id} value={pm.id}>
                            {pm.name}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </Grid>
                </Grid>
                <Grid item container xs={12} lg={6} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('Processing Type')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      name="onlineOrderProcessingType"
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (selected === '') {
                            return <span style={{ color: '#888', fontStyle: 'italic' }}>Not selected</span>;
                          }
                          return selected;
                        },
                      }}
                      onChange={(event) => {
                        setData({ ...data, online_order_processing_type: event.target.value });
                      }}
                      value={data?.online_order_processing_type ?? ''}
                    >
                      <MenuItem key="none" value={''}>
                        Not select
                      </MenuItem>
                      {ONLINE_ORDER_PROCESSING_TYPE.map((type) => {
                        return (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </Grid>
                </Grid>
                <Grid item container xs={12} lg={6} display="flex" alignItems="center">
                  <Grid item xs={6}>
                    <span className={styles.fieldTitle}>{tCommon('Payment collection')}</span>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      name="paymentCollectionMode"
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onChange={(event) => {
                        setData({ ...data, config: { ...data.config, payment_collection_mode: event.target.value } });
                      }}
                      value={data?.config?.payment_collection_mode || DEFAULT_ORDER_PAYMENT_COLLECTION_MODE}
                    >
                      {Object.keys(ORDER_PAYMENT_COLLECTION_MODES).map((mode) => {
                        return (
                          <MenuItem key={mode} value={mode}>
                            {ORDER_PAYMENT_COLLECTION_MODES[mode]}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </Grid>
                </Grid>
                {data?.config?.payment_collection_mode === 'ADVANCE_PAYMENT' && (
                  <>
                    <Grid item container xs={12} lg={6} display="flex" alignItems="center">
                      <Grid item xs={6}>
                        <span className={styles.fieldTitle}>{tCommon('Advance Payment Type')}</span>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          select
                          name="advancePaymentType"
                          size="small"
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onChange={(event) => {
                            setData({ ...data, config: { ...data.config, advance_payment_type: event.target.value } });
                          }}
                          value={data?.config?.advance_payment_type || ''}
                        >
                          {Object.keys(ADVANCE_PAYMENT_TYPES).map((type) => {
                            return (
                              <MenuItem key={type} value={type}>
                                {ADVANCE_PAYMENT_TYPES[type]}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                      </Grid>
                    </Grid>
                    <Grid item container xs={12} lg={6} display="flex" alignItems="center">
                      <Grid item xs={6}>
                        <span className={styles.fieldTitle}>{tCommon('Advance Payment Amount')}</span>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          name="advancePaymentAmount"
                          size="small"
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onChange={(event) => {
                            setData({
                              ...data,
                              config: { ...data.config, advance_payment_amount: event.target.value },
                            });
                          }}
                          value={data?.config?.advance_payment_amount || ''}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </>
            )}
            <Grid item xs={12} className={styles.subHeader}>
              <Typography variant="h8" gutterBottom>
                {tCommon('whatsapp_notifications')}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                {tCommon('whatsapp_notifications_desc')}
              </Typography>
            </Grid>
            <Grid item container xs={12} lg={6} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>{tCommon('whatsapp_phone_number_id')}</span>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="whatsappPhoneNumberId"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData((prevData) => ({
                      ...prevData,
                      config: {
                        ...prevData.config,
                        whatsapp_phone_number_id: event.target.value,
                      },
                    }));
                  }}
                  value={data?.config?.whatsapp_phone_number_id || ''}
                />
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={6} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>{tCommon('whatsapp_access_token')}</span>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="whatsappAccessToken"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData((prevData) => ({
                      ...prevData,
                      config: {
                        ...prevData.config,
                        whatsapp_access_token: event.target.value,
                      },
                    }));
                  }}
                  value={data?.config?.whatsapp_access_token || ''}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} className={styles.BillHeader} justifyContent="flex-end">
              <Grid item xs={12} sm={3}>
                <Button
                  alignItems="end"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={loadingIndicator}
                  onClick={updateShopSettings}
                >
                  {loadingIndicator && !updateTerminalSettingsDialog ? (
                    <>
                      <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                      {tCommon('Updating...')}
                    </>
                  ) : (
                    tCommon('Update')
                  )}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <hr className={styles.divider} />
          <Grid item container xs={12} columnSpacing={1} rowSpacing={1.5} className={styles.sessionContainer}>
            <Grid item xs={12} className={styles.subHeader}>
              <Typography variant="h8" gutterBottom>
                {tCommon('Shop Type')}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                {tCommon('This helps auto-configure settings tailored to your business needs.')}
              </Typography>
            </Grid>
            <Grid item container xs={12} lg={6} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>{tCommon('Category')}</span>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  name="shopCategory"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({
                      ...data,
                      category: event.target.value,
                      sub_category: '', // Reset subcategory on category change
                    });
                  }}
                  value={data?.category || ''}
                >
                  {SHOP_CATEGORIES.map((category) => {
                    let [key, value] = Object.entries(category)[0];
                    return (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            </Grid>
            <Grid item container xs={12} lg={6} display="flex" alignItems="center">
              <Grid item xs={6}>
                <span className={styles.fieldTitle}>{tCommon('Subcategory')}</span>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  name="shopSubCategory"
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onChange={(event) => {
                    setData({
                      ...data,
                      sub_category: event.target.value,
                    });
                  }}
                  value={data?.sub_category || ''}
                >
                  {(SHOP_SUB_CATEGORIES[data?.category?.toLowerCase()] || SHOP_SUB_CATEGORIES['other']).map(
                    (sub_category) => {
                      let [key, value] = Object.entries(sub_category)[0];
                      return (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      );
                    }
                  )}
                </TextField>
              </Grid>
            </Grid>
            <Grid item xs={12} className={styles.BillHeader} justifyContent="flex-end">
              <Grid item xs={12} sm={3}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={styles.button}
                  disabled={!isChangeShopCategory() || loadingIndicator}
                  onClick={() => setUpdateTerminalSettingsDialog(true)}
                >
                  {tCommon('Update')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} className={styles.BillHeader} display="flex" alignItems="center">
            <Grid item xs={6} lg={3}>
              <span className={styles.fieldTitle}>{tCommon('Enable Support')}</span>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={supportEnabled} onChange={handleChangeSupportMode} color="primary" />}
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} style={{ display: 'none' }}>
            <Grid item xs={6} lg={3}>
              <span className={styles.fieldTitle}>{tCommon('External Token Key')}</span>
            </Grid>
            <Grid item xs={6} lg={9}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <TextField
                    id="extTkn"
                    name="externalTokenKey"
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    value={externalTokenKey}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Button
                    variant="contained"
                    style={{ width: '100%' }}
                    onClick={copyExternalToken}
                    disabled={loadingIndicator}
                  >
                    {tCommon('Copy')}
                  </Button>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Button
                    variant="contained"
                    color="secondary"
                    style={{ backgroundColor: '#00a65a', width: '100%' }}
                    onClick={resetExtTknBtnPressed}
                    disabled={loadingIndicator}
                  >
                    {tCommon('Reset')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {featureNotAvailable && (
            <FeatureNotAvailableDialog closeHandler={closeDialogHandler} feature={featureNotAvailable} />
          )}
          {updateTerminalSettingsDialog && (
            <ConfirmPopup
              data={updateTerminalSettingsDialog}
              handleClose={() => setUpdateTerminalSettingsDialog(false)}
              handleOkay={handleTerminalSettingsUpdate}
              alertTitle={tManagement('Change terminal settings')}
              alertContent={tManagement(
                'Terminal settings updated for specific shop type and subtype. Enjoy optimized experience.'
              )}
              isLoading={loadingIndicator}
            />
          )}
          {showAddIpDialog && (
            <AddIpDialog
              selectedPrinterIp={selectedPrinterIp}
              handleClose={() => {
                setShowAddIpDialog(false);
                setSelectedPrinterIp(null);
              }}
              handleSuccess={(newPrinterIp) => {
                const currentIps = data?.config?.ip_printers || [];
                let newPrinterIps;
                if (selectedPrinterIp !== null && selectedPrinterIp.index !== undefined) {
                   newPrinterIps = [...currentIps];
                   newPrinterIps[selectedPrinterIp.index] = {
                     ...newPrinterIps[selectedPrinterIp.index],
                     ...newPrinterIp,
                   };
                } else {
                  newPrinterIps = [...currentIps, newPrinterIp];
                }
                const updatedShopData = { ...data, config: { ...data.config, ip_printers: newPrinterIps } };
                setData(updatedShopData);
                updateShopSettings(updatedShopData);
              }}
            />
          )}
          {showAddTemplateDialog && (
            <AddCustomPrnTemplateDialog
              selectedTemplate={selectedTemplateForEdit}
              handleClose={() => {
                setShowAddTemplateDialog(false);
                setSelectedTemplateForEdit(null);
              }}
              handleSuccess={handleSaveTemplateSuccess}
            />
          )}
        </Grid>
      </div>
    </div>
  );
}

export default ShopSettings;
